import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Ticket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "./Reveal";
import { ASSETS } from "@/lib/data";

const TARGET = new Date("2026-11-25T09:00:00+08:00").getTime();

const useCountdown = () => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(TARGET - Date.now(), 0);
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const Unit = ({ v, l }) => (
  <div className="flex flex-col items-center">
    <div className="min-w-[3.5rem] rounded-xl border border-white/10 bg-void/70 px-3 py-6 text-center font-display text-3xl text-white sm:min-w-[4.5rem] sm:py-9 sm:text-5xl">
      {String(v).padStart(2, "0")}
    </div>
    <span className="mt-2 font-mono-cap text-[9px] text-slate-400">{l}</span>
  </div>
);

export const EventTeaser = () => {
  const { d, h, m, s } = useCountdown();
  const navigate = useNavigate();
  return (
    <section id="event" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8">
      <Reveal className="relative overflow-hidden rounded-3xl border border-neon-violet/40 bg-gradient-to-br from-surface to-void px-8 py-6 glow-ring sm:px-14 sm:py-8">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-neon-pink/20 blur-[90px]" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-neon-cyan/20 blur-[90px]" />

        <div className="relative flex flex-col items-center justify-between gap-8 text-center lg:flex-row lg:items-center lg:text-left">
          <div className="flex flex-col items-center lg:items-start">
            <p className="font-mono-cap mb-4 text-[11px] text-neon-cyan">Upcoming Fight Night</p>
            <div className="relative w-fit">
              <img
                src={ASSETS.riseLogo}
                alt="The Rise of Pahlawan"
                className="h-auto w-[300px] object-contain sm:w-[440px] lg:w-[520px]"
                data-testid="event-rise-logo"
              />
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-6 lg:justify-start">
              <span className="flex items-center gap-2 font-sub text-sm text-slate-200">
                <CalendarDays className="h-4 w-4 text-neon-pink" /> 25–28 November 2026
              </span>
              <span className="flex items-center gap-2 font-sub text-sm text-slate-200">
                <MapPin className="h-4 w-4 text-neon-cyan" /> Stadium Titiwangsa, Kuala Lumpur
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-6 lg:items-end">
            <img
              src={ASSETS.eventLogo}
              alt="WPIMC 3.0"
              className="h-auto w-[220px] object-contain sm:w-[300px]"
              style={{ filter: "drop-shadow(0 0 30px rgba(0,247,255,0.35))" }}
              data-testid="event-logo"
            />
            <div className="flex gap-3 sm:gap-4" data-testid="event-countdown">
              <Unit v={d} l="Days" />
              <Unit v={h} l="Hrs" />
              <Unit v={m} l="Min" />
              <Unit v={s} l="Sec" />
            </div>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate("/register")}
              className="flex w-full items-center justify-center gap-3 whitespace-nowrap rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-14 py-5 font-sub text-lg font-bold uppercase tracking-widest text-white shadow-[0_0_45px_rgba(255,0,216,0.35)] sm:w-auto"
              data-testid="event-tickets-btn"
            >
              <Ticket className="h-5 w-5" /> Amateur Registration
            </motion.button>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
